import { useMediaQuery, useMutationObserver, watchImmediate } from "@vueuse/core";
import { computed, ref } from "vue";

function getPropertyValue(propery) {
  return window.getComputedStyle(document.documentElement).getPropertyValue(propery);
}

/**
 * Create a callback function to be called when the breakpoint changes
 * and update the receiver ref with the new value.
 *
 * It also watches the media query to update the receiver ref when the
 * media query changes.
 *
 * It keeps track of the watcher to unwatch it when the breakpoint changes.
 *
 * @param {import("vue").Ref} receiver
 * @param {import("vue").Ref} breakpoint
 * @returns Function
 */
function createOnChangeBreakpointCallback(receiver, breakpoint) {
  let unwatch;

  return () => {
    unwatch?.();

    const watched = useMediaQuery(`(min-width: ${breakpoint.value})`);

    unwatch = watchImmediate(watched, (value) => {
      receiver.value = value;
    });
  };
}

/**
 * A composable to get the current breakpoints and if the screen is greater than
 * a specific breakpoint.
 *
 * @returns {{
 *  smBreakpoint: import("vue").Ref<string>,
 *  mdBreakpoint: import("vue").Ref<string>,
 *  lgBreakpoint: import("vue").Ref<string>,
 *  xlBreakpoint: import("vue").Ref<string>,
 *  isSmScreen: import("vue").ComputedRef<boolean>,
 *  isMdScreen: import("vue").ComputedRef<boolean>,
 *  isLgScreen: import("vue").ComputedRef<boolean>,
 *  isXlScreen: import("vue").ComputedRef<boolean>,
 *  isGtSmScreen: import("vue").Ref<boolean>,
 *  isGtMdScreen: import("vue").Ref<boolean>,
 *  isGtXlScreen: import("vue").Ref<boolean>,
 * }}
 */
export function useBreakpoints() {
  const smBreakpoint = ref("");
  const isGtSmScreen = ref(false);
  watchImmediate(smBreakpoint, createOnChangeBreakpointCallback(isGtSmScreen, smBreakpoint));

  const mdBreakpoint = ref("");
  const isGtMdScreen = ref(false);
  watchImmediate(mdBreakpoint, createOnChangeBreakpointCallback(isGtMdScreen, mdBreakpoint));

  const lgBreakpoint = ref("");
  const isGtLgScreen = ref(false);
  watchImmediate(lgBreakpoint, createOnChangeBreakpointCallback(isGtLgScreen, lgBreakpoint));

  const xlBreakpoint = ref("");
  const isGtXlScreen = ref(false);
  watchImmediate(xlBreakpoint, createOnChangeBreakpointCallback(isGtXlScreen, xlBreakpoint));

  const { stop } = useMutationObserver(document.documentElement, setBreakpointValues, {
    subtree: true,
    attributes: true,
    attributeFilter: ["class", "style"],
  });

  /**
   * Set the breakpoint values from the CSS variables.
   */
  function setBreakpointValues() {
    smBreakpoint.value = getPropertyValue("--sp-ref-breakpoint-sm");
    mdBreakpoint.value = getPropertyValue("--sp-ref-breakpoint-md");
    lgBreakpoint.value = getPropertyValue("--sp-ref-breakpoint-lg");
    xlBreakpoint.value = getPropertyValue("--sp-ref-breakpoint-xl");

    // Stop the observer when the breakpoints are set, this is only needed once
    if (smBreakpoint.value && mdBreakpoint.value && lgBreakpoint.value && xlBreakpoint.value) {
      stop?.();
    }
  }

  const isSmScreen = computed(() => isGtSmScreen.value === false);
  const isMdScreen = computed(() => isGtSmScreen.value === true && isGtMdScreen.value === false);
  const isLgScreen = computed(() => isGtMdScreen.value === true && isGtLgScreen.value === false);
  const isXlScreen = computed(() => isGtXlScreen.value === true);

  // Detect the breakpoints when the composable is created, this speeds up the initial detection
  setBreakpointValues();

  return {
    smBreakpoint,
    mdBreakpoint,
    lgBreakpoint,
    xlBreakpoint,
    isSmScreen,
    isMdScreen,
    isLgScreen,
    isXlScreen,
    isGtSmScreen,
    isGtMdScreen,
    isGtLgScreen,
    isGtXlScreen,
  };
}
